import { formatLocalDate } from "../lib/utils";
import { useState, useEffect } from "react";
import { MainLayout } from "../components/Layout";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import {
  Inbox,
  Calendar,
  Phone,
  Mail,
  Smartphone,
  CheckCircle2,
  XCircle,
  Clock,
  Plus,
  ArrowRight,
  User,
  Loader2,
  RefreshCw,
  Wrench,
  Eye,
  Trash2,
  ChevronLeft,
  ChevronRight,
  CalendarDays,
  Camera,
  QrCode,
  Upload,
  Image
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { QRCodeSVG } from "qrcode.react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const BRANDS = [
  "Apple", "Samsung", "Huawei", "Xiaomi", "Oppo", "Vivo", "OnePlus", 
  "Google", "Sony", "LG", "Motorola", "Nokia", "Realme", "Honor", "Asus", "Autre"
];

const BRAND_SERIAL_CONFIG = {
  "apple": { imei: 15, serial: 12 },
  "samsung": { imei: 15, serial: 11 },
  "huawei": { imei: 15, serial: 16 },
  "xiaomi": { imei: 15, serial: 16 },
  "default": { imei: 15, serial: 10 }
};

const STATUS_CONFIG = {
  pending: { label: "En attente", color: "bg-amber-500/20 text-amber-400 border-amber-500/30", dotColor: "bg-amber-500" },
  confirmed: { label: "Confirmée", color: "bg-emerald-500/20 text-emerald-400 border-emerald-500/30", dotColor: "bg-emerald-500" },
  cancelled: { label: "Annulée", color: "bg-red-500/20 text-red-400 border-red-500/30", dotColor: "bg-red-500" },
  date_proposed: { label: "Date proposée — En attente client", color: "bg-violet-500/20 text-violet-400 border-violet-500/30", dotColor: "bg-violet-500" },
  date_counter_proposed: { label: "Contre-proposition client", color: "bg-orange-500/20 text-orange-400 border-orange-500/30", dotColor: "bg-orange-500" },
  converted: { label: "Convertie", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", dotColor: "bg-blue-500" },
  converted: { label: "En réparation", color: "bg-blue-500/20 text-blue-400 border-blue-500/30", dotColor: "bg-blue-500" },
  completed: { label: "Terminée ✓", color: "bg-teal-500/20 text-teal-400 border-teal-500/30", dotColor: "bg-teal-500" }
};

const DAYS_OF_WEEK = ["Dim", "Lun", "Mar", "Mer", "Jeu", "Ven", "Sam"];
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];

// Generate time slots from 9h to 17h (30 min each)
// Créneaux client (formulaire nouvelle demande) : horaires d'ouverture 9h–19h
const generateTimeSlots = () => {
  const slots = [];
  for (let h = 9; h < 19; h++) {
    for (let m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      slots.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}` });
    }
  }
  return slots;
};
const TIME_SLOTS = generateTimeSlots();

// Créneaux admin (proposition date) : 6h–22h, hors horaires inclus
const generateAdminSlots = () => {
  const slots = [];
  for (let h = 6; h < 22; h++) {
    for (let m of [0, 30]) {
      const hh = String(h).padStart(2, "0");
      const mm = String(m).padStart(2, "0");
      const horsHoraires = (h < 9 || h >= 19) ? " ⚡ hors horaires" : "";
      slots.push({ value: `${hh}:${mm}`, label: `${hh}:${mm}${horsHoraires}` });
    }
  }
  return slots;
};
const ADMIN_SLOTS = generateAdminSlots();

// Rétrocompat affichage
const fmtSlot = (v) => ({ morning:"Matin (9h–12h)", afternoon:"Après-midi (14h–17h)", evening:"Fin de journée (17h–19h)" }[v] || v || "—");


export default function IntakeRequestsPage() {
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [view, setView] = useState("list"); // "list" or "calendar"
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [proposeDateDialogOpen, setProposeDateDialogOpen] = useState(false);
  const [proposeTarget, setProposeTarget] = useState(null); // {request, type: "intake"}
  const [proposedDate, setProposedDate] = useState("");
  const [proposedTime, setProposedTime] = useState("09:00");
  const [proposedMessage, setProposedMessage] = useState("");
  const [proposeSubmitting, setProposeSubmitting] = useState(false);
  const [convertDialogOpen, setConvertDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [serialNumber, setSerialNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  
  // Photo upload for conversion
  const [conversionPhotos, setConversionPhotos] = useState([]);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [qrCodeLoading, setQrCodeLoading] = useState(false);
  const [showQrCode, setShowQrCode] = useState(false);
  
  // Calendar state
  const [currentMonth, setCurrentMonth] = useState(new Date());

  // Form for manual intake
  const [newRequest, setNewRequest] = useState({
    client_first_name: "",
    client_last_name: "",
    client_phone: "",
    client_email: "",
    device_brand: "",
    device_model: "",
    problem_description: "",
    preferred_date: "",
    preferred_time_slot: "09:00"
  });

  const fetchRequests = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(`${API_URL}/api/admin/intake-requests`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRequests(res.data);
    } catch (err) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRequests();
  }, []);

  const filteredRequests = filter === "all" 
    ? requests 
    : requests.filter(r => r.status === filter);

  const counts = {
    all: requests.length,
    pending: requests.filter(r => r.status === "pending").length,
    confirmed: requests.filter(r => r.status === "confirmed").length,
    cancelled: requests.filter(r => r.status === "cancelled").length,
    date_proposed: requests.filter(r => r.status === "date_proposed").length,
    date_counter_proposed: requests.filter(r => r.status === "date_counter_proposed").length,
    converted: requests.filter(r => r.status === "converted").length,
    completed: requests.filter(r => r.status === "completed").length
  };

  const handleProposeDate = async () => {
    if (!proposedDate || !proposeTarget) return;
    setProposeSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      await axios.post(
        `${API_URL}/api/admin/intake-requests/${proposeTarget.request.id}/propose-date`,
        {},
        {
          params: { proposed_date: proposedDate, proposed_time_slot: proposedTime, message: proposedMessage },
          headers: { Authorization: `Bearer ${token}` }
        }
      );
      toast.success("Nouvelle date proposée au client par email");
      setProposeDateDialogOpen(false);
      setProposedDate("");
      setProposedMessage("");
      fetchRequests();
    } catch (err) {
      toast.error("Erreur lors de la proposition de date");
    } finally {
      setProposeSubmitting(false);
    }
  };

  const handleStatusChange = async (request, newStatus) => {
    try {
      const token = localStorage.getItem("token");
      await axios.put(
        `${API_URL}/api/admin/intake-requests/${request.id}?status=${newStatus}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(`Statut mis à jour: ${STATUS_CONFIG[newStatus].label}`);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const handleDelete = async () => {
    if (!selectedRequest) return;
    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      await axios.delete(`${API_URL}/api/admin/intake-requests/${selectedRequest.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Demande supprimée");
      setDeleteDialogOpen(false);
      setSelectedRequest(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddRequest = async () => {
    if (!newRequest.client_first_name || !newRequest.client_last_name || !newRequest.client_phone || !newRequest.device_brand || !newRequest.problem_description || !newRequest.preferred_date) {
      toast.error("Veuillez remplir tous les champs obligatoires");
      return;
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      const submitData = {
        ...newRequest,
        client_name: `${newRequest.client_first_name} ${newRequest.client_last_name}`
      };
      await axios.post(`${API_URL}/api/admin/intake-requests/manual`, submitData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Demande ajoutée");
      setAddDialogOpen(false);
      setNewRequest({
        client_first_name: "",
        client_last_name: "",
        client_phone: "",
        client_email: "",
        device_brand: "",
        device_model: "",
        problem_description: "",
        preferred_date: "",
        preferred_time_slot: "09:00"
      });
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  const handleConvertToRepair = async () => {
    if (!selectedRequest) return;

    // Check mandatory photo
    if (conversionPhotos.length === 0) {
      toast.error("Veuillez ajouter au moins une photo de l'appareil");
      return;
    }

    if (serialNumber) {
      const brand = (selectedRequest.device_brand || "default").toLowerCase();
      const config = BRAND_SERIAL_CONFIG[brand] || BRAND_SERIAL_CONFIG["default"];
      const len = serialNumber.length;
      if (len !== config.imei && len !== config.serial) {
        toast.error(`Le numéro de série doit contenir ${config.imei} (IMEI) ou ${config.serial} caractères`);
        return;
      }
    }

    setSubmitting(true);
    try {
      const token = localStorage.getItem("token");
      
      // First create the repair
      const res = await axios.post(
        `${API_URL}/api/admin/intake-requests/${selectedRequest.id}/convert`,
        {},
        { 
          params: { serial_number: serialNumber },
          headers: { Authorization: `Bearer ${token}` } 
        }
      );
      
      const repairId = res.data.repair_id;
      
      // Upload photos to the newly created repair
      for (const photo of conversionPhotos) {
        try {
          await axios.post(
            `${API_URL}/api/repairs/${repairId}/photos`,
            {
              image_data: photo.data,
              photo_type: "before",
              description: "Photo de prise en charge"
            },
            { headers: { Authorization: `Bearer ${token}` } }
          );
        } catch (photoErr) {
          console.error("Error uploading photo:", photoErr);
        }
      }
      
      toast.success(`Réparation créée: ${res.data.ticket_number}`);
      setConvertDialogOpen(false);
      setSerialNumber("");
      setConversionPhotos([]);
      setShowQrCode(false);
      setQrCodeData(null);
      fetchRequests();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSubmitting(false);
    }
  };

  // Handle photo upload for conversion
  const handleConversionPhotoUpload = (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    Array.from(files).forEach(file => {
      const reader = new FileReader();
      reader.onload = (e) => {
        setConversionPhotos(prev => [...prev, {
          id: Date.now() + Math.random(),
          data: e.target.result,
          name: file.name
        }]);
      };
      reader.readAsDataURL(file);
    });
    e.target.value = "";
  };

  const removeConversionPhoto = (photoId) => {
    setConversionPhotos(prev => prev.filter(p => p.id !== photoId));
  };

  // Generate QR code for mobile upload using real backend token
  const generateConversionQrCode = async () => {
    if (!selectedRequest) return;
    
    setQrCodeLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await axios.post(
        `${API_URL}/api/admin/intake-requests/${selectedRequest.id}/mobile-upload-token`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Use the VPS URL directly
      setQrCodeData({
        url: `https://app.amcphone-martinique.fr/mobile-upload/${res.data.token}`,
        token: res.data.token,
        expiresAt: res.data.expires_at
      });
      setShowQrCode(true);
      toast.info("Scannez le QR code avec votre téléphone pour ajouter des photos");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la génération du QR code");
    } finally {
      setQrCodeLoading(false);
    }
  };
  
  // Poll for photos uploaded via QR code
  const checkForUploadedPhotos = async () => {
    if (!selectedRequest || !qrCodeData?.token) return;
    
    try {
      const token = localStorage.getItem("token");
      const res = await axios.get(
        `${API_URL}/api/admin/intake-requests/${selectedRequest.id}/photos`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (res.data && res.data.length > 0) {
        // Convert to our photo format
        const newPhotos = res.data.map(p => ({
          id: p.id,
          data: `${API_URL}${p.url}`,
          name: "Photo mobile",
          fromQr: true
        }));
        
        // Add new photos that aren't already in the list
        setConversionPhotos(prev => {
          const existingIds = prev.map(p => p.id);
          const photosToAdd = newPhotos.filter(p => !existingIds.includes(p.id));
          if (photosToAdd.length > 0) {
            toast.success(`${photosToAdd.length} photo(s) reçue(s) via QR code!`);
            return [...prev, ...photosToAdd];
          }
          return prev;
        });
      }
    } catch (err) {
      console.error("Error checking uploaded photos:", err);
    }
  };
  
  // Poll every 3 seconds when QR code is shown
  useEffect(() => {
    let interval;
    if (showQrCode && qrCodeData?.token) {
      interval = setInterval(checkForUploadedPhotos, 3000);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [showQrCode, qrCodeData?.token, selectedRequest]);

  const openDetailDialog = (request) => {
    setSelectedRequest(request);
    setDetailDialogOpen(true);
  };

  const openConvertDialog = (request) => {
    setSelectedRequest(request);
    setSerialNumber(request.serial_number || "");
    setConversionPhotos([]);
    setShowQrCode(false);
    setQrCodeData(null);
    setConvertDialogOpen(true);
  };

  const openDeleteDialog = (request) => {
    setSelectedRequest(request);
    setDeleteDialogOpen(true);
  };

  // Calendar functions
  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();
    const startingDay = firstDay.getDay();
    return { daysInMonth, startingDay };
  };

  const formatDateKey = (year, month, day) => {
    return `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  };

  const getRequestsForDate = (dateKey) => {
    return requests.filter(r => r.preferred_date === dateKey);
  };

  const renderCalendar = () => {
    const { daysInMonth, startingDay } = getDaysInMonth(currentMonth);
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const days = [];

    // Empty cells for days before the first day of the month
    for (let i = 0; i < startingDay; i++) {
      days.push(<div key={`empty-${i}`} className="h-24 border border-border/30 bg-secondary/10"></div>);
    }

    // Days of the month
    for (let day = 1; day <= daysInMonth; day++) {
      const date = new Date(year, month, day);
      const dateKey = formatDateKey(year, month, day);
      const dayRequests = getRequestsForDate(dateKey);
      const isWeekend = date.getDay() === 0 || date.getDay() === 6;
      const isToday = new Date().toDateString() === date.toDateString();

      days.push(
        <div 
          key={day} 
          className={`h-24 border border-border/30 p-1 overflow-hidden
            ${isWeekend ? 'bg-red-500/5' : 'bg-card/50'}
            ${isToday ? 'ring-2 ring-primary ring-inset' : ''}
          `}
        >
          <div className={`text-xs font-medium mb-1 ${isWeekend ? 'text-red-400/50' : 'text-muted-foreground'}`}>
            {day}
          </div>
          <div className="space-y-0.5">
            {dayRequests.slice(0, 3).map(req => (
              <div 
                key={req.id}
                onClick={() => openDetailDialog(req)}
                className={`text-[10px] px-1 py-0.5 rounded truncate cursor-pointer hover:opacity-80 transition-opacity
                  ${req.status === 'pending' ? 'bg-amber-500/20 text-amber-400' : ''}
                  ${req.status === 'confirmed' ? 'bg-emerald-500/20 text-emerald-400' : ''}
                  ${req.status === 'cancelled' ? 'bg-red-500/20 text-red-400' : ''}
                  ${req.status === 'date_proposed' ? 'bg-violet-500/20 text-violet-400' : ''}
                  ${req.status === 'date_counter_proposed' ? 'bg-orange-500/20 text-orange-400' : ''}
                  ${req.status === 'converted' ? 'bg-blue-500/20 text-blue-400' : ''}
                  ${req.status === 'completed' ? 'bg-teal-500/20 text-teal-400' : ''}
                `}
              >
                {req.preferred_time_slot} - {req.client_name?.split(' ')[0]}
              </div>
            ))}
            {dayRequests.length > 3 && (
              <div className="text-[10px] text-muted-foreground text-center">
                +{dayRequests.length - 3} autres
              </div>
            )}
          </div>
        </div>
      );
    }

    return days;
  };

  const getMinDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Check if date is weekend
  const isWeekend = (dateStr) => {
    const date = new Date(dateStr);
    const day = date.getDay();
    return day === 0 || day === 6;
  };

  return (
    <MainLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold font-chivo text-foreground flex items-center gap-2">
              <Inbox className="w-6 h-6 text-primary" />
              Demandes de prise en charge
            </h1>
            <p className="text-muted-foreground text-sm mt-1">
              Gérez les demandes de réparation et les créneaux
            </p>
          </div>
          <div className="flex gap-2">
            <Button 
              variant={view === "list" ? "default" : "outline"} 
              size="sm"
              onClick={() => setView("list")}
            >
              <Inbox className="w-4 h-4 mr-2" />
              Liste
            </Button>
            <Button 
              variant={view === "calendar" ? "default" : "outline"} 
              size="sm"
              onClick={() => setView("calendar")}
            >
              <CalendarDays className="w-4 h-4 mr-2" />
              Calendrier
            </Button>
            <Button variant="outline" onClick={fetchRequests} disabled={loading}>
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? "animate-spin" : ""}`} />
              Actualiser
            </Button>
            <Button onClick={() => setAddDialogOpen(true)} className="bg-primary">
              <Plus className="w-4 h-4 mr-2" />
              Ajouter
            </Button>
          </div>
        </div>

        {/* Filters */}
        <div className="flex gap-2 flex-wrap">
          {[
            { key: "all", label: "Toutes" },
            { key: "pending", label: "En attente", dotColor: "bg-amber-500" },
            { key: "confirmed", label: "Confirmées", dotColor: "bg-emerald-500" },
            { key: "converted", label: "En réparation", dotColor: "bg-blue-500" },
            { key: "completed", label: "Terminées", dotColor: "bg-teal-500" },
            { key: "cancelled", label: "Annulées", dotColor: "bg-red-500" }
          ].map(f => (
            <Button
              key={f.key}
              variant={filter === f.key ? "default" : "outline"}
              size="sm"
              onClick={() => setFilter(f.key)}
              className={filter === f.key ? "bg-primary" : ""}
            >
              {f.dotColor && <span className={`w-2 h-2 rounded-full ${f.dotColor} mr-2`}></span>}
              {f.label} ({counts[f.key]})
            </Button>
          ))}
        </div>

        {/* Calendar View */}
        {view === "calendar" && (
          <Card className="bg-card border-border">
            <CardHeader className="pb-2">
              <div className="flex items-center justify-between">
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                  <ChevronLeft className="w-5 h-5" />
                </Button>
                <CardTitle className="text-lg">
                  {MONTHS[currentMonth.getMonth()]} {currentMonth.getFullYear()}
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                  <ChevronRight className="w-5 h-5" />
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {/* Days header */}
              <div className="grid grid-cols-7 gap-0 mb-1">
                {DAYS_OF_WEEK.map(day => (
                  <div key={day} className={`h-8 flex items-center justify-center text-xs font-medium border border-border/30 bg-secondary/30 ${day === 'Dim' || day === 'Sam' ? 'text-red-400' : 'text-muted-foreground'}`}>
                    {day}
                  </div>
                ))}
              </div>
              {/* Calendar grid */}
              <div className="grid grid-cols-7 gap-0">
                {renderCalendar()}
              </div>
              {/* Legend */}
              <div className="flex gap-4 mt-4 text-xs text-muted-foreground">
                <div className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-amber-500/20"></span>
                  En attente
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-emerald-500/20"></span>
                  Confirmée
                </div>
                <div className="flex items-center gap-1">
                  <span className="w-3 h-3 rounded bg-red-500/20"></span>
                  Annulée
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* List View */}
        {view === "list" && (
          <>
            {loading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : filteredRequests.length === 0 ? (
              <Card className="bg-card border-border">
                <CardContent className="py-12 text-center">
                  <Inbox className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">Aucune demande de prise en charge</p>
                </CardContent>
              </Card>
            ) : (
              <Card className="bg-card border-border overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-border bg-secondary/30">
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Client</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Contact</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Appareil</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Créneau</th>
                        <th className="text-left p-3 text-sm font-medium text-muted-foreground">Statut</th>
                        <th className="text-right p-3 text-sm font-medium text-muted-foreground">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredRequests.map(request => {
                        const status = STATUS_CONFIG[request.status] || STATUS_CONFIG.pending;
                        return (
                          <tr key={request.id} className="border-b border-border/50 hover:bg-secondary/20 transition-colors">
                            <td className="p-3">
                              <p className="font-medium text-foreground">{request.client_name}</p>
                              <p className="text-xs text-muted-foreground">
                                {new Date(request.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "short" })}
                              </p>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                                <Phone className="w-3 h-3" />
                                {request.client_phone}
                              </div>
                              {request.client_email && (
                                <div className="flex items-center gap-1 text-xs text-muted-foreground truncate max-w-[150px]">
                                  <Mail className="w-3 h-3" />
                                  {request.client_email}
                                </div>
                              )}
                            </td>
                            <td className="p-3">
                              <p className="text-sm">{request.device_brand} {request.device_model}</p>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center gap-1 text-sm">
                                <Calendar className="w-3 h-3 text-muted-foreground" />
                                {formatLocalDate(request.preferred_date)}
                              </div>
                              <div className="flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock className="w-3 h-3" />
                                {request.preferred_time_slot}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded-full text-xs border ${status.color}`}>
                                {status.label}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-end gap-1">
                                <Button variant="ghost" size="sm" onClick={() => openDetailDialog(request)} title="Voir détails">
                                  <Eye className="w-4 h-4" />
                                </Button>
                                {request.status === "pending" && (
                                  <>
                                    <Button 
                                      variant="ghost" 
                                      size="sm" 
                                      className="text-emerald-500 hover:text-emerald-400 hover:bg-emerald-500/10"
                                      onClick={() => handleStatusChange(request, "confirmed")}
                                      title="Valider"
                                    >
                                      <CheckCircle2 className="w-4 h-4" />
                                    </Button>
                                    <Button 
                                      variant="ghost" 
                                      size="sm"
                                      className="text-red-500 hover:text-red-400 hover:bg-red-500/10"
                                      onClick={() => handleStatusChange(request, "cancelled")}
                                      title="Annuler"
                                    >
                                      <XCircle className="w-4 h-4" />
                                    </Button>
                                  </>
                                )}
                                {request.status === "confirmed" && (
                                  <Button 
                                    variant="ghost" 
                                    size="sm"
                                    className="text-primary hover:text-primary/80"
                                    onClick={() => openConvertDialog(request)}
                                    title="Convertir en réparation"
                                  >
                                    <ArrowRight className="w-4 h-4" />
                                  </Button>
                                )}
                                <Button 
                                  variant="ghost" 
                                  size="sm"
                                  className="text-destructive hover:text-destructive/80 hover:bg-destructive/10"
                                  onClick={() => openDeleteDialog(request)}
                                  title="Supprimer"
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </Card>
            )}
          </>
        )}

        {/* Detail Dialog */}
        <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <User className="w-5 h-5 text-primary" />
                Détails de la demande
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <div className="flex items-center justify-between">
                  <span className={`px-3 py-1 rounded-full text-sm border ${STATUS_CONFIG[selectedRequest.status]?.color}`}>
                    {STATUS_CONFIG[selectedRequest.status]?.label}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    Créé le {new Date(selectedRequest.created_at).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit" })}
                  </span>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Client</p>
                    <p className="font-medium">{selectedRequest.client_name}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Téléphone</p>
                    <p className="font-medium">{selectedRequest.client_phone}</p>
                  </div>
                </div>

                {selectedRequest.client_email && (
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Email</p>
                    <p>{selectedRequest.client_email}</p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Appareil</p>
                    <p>{selectedRequest.device_brand} {selectedRequest.device_model}</p>
                  </div>
                  <div className="bg-secondary/30 rounded-lg p-3">
                    <p className="text-xs text-muted-foreground mb-1">Créneau réservé</p>
                    <p>{formatLocalDate(selectedRequest.preferred_date)}</p>
                    <p className="text-sm text-muted-foreground">{selectedRequest.preferred_time_slot}</p>
                  </div>
                </div>

                <div className="bg-secondary/30 rounded-lg p-3">
                  <p className="text-xs text-muted-foreground mb-1">Problème décrit</p>
                  <p className="text-sm">{selectedRequest.problem_description}</p>
                </div>
                {/* Contre-proposition client */}
                {selectedRequest.status === "date_counter_proposed" && selectedRequest.counter_date && (
                  <div className="p-3 rounded-lg bg-orange-500/10 border border-orange-500/40">
                    <p className="text-xs font-bold text-orange-400 mb-2 flex items-center gap-1">
                      <CalendarDays className="w-3 h-3" /> Contre-proposition du client
                    </p>
                    <p className="text-sm font-bold text-foreground">{selectedRequest.counter_date}</p>
                    <p className="text-xs text-muted-foreground">
                      {fmtSlot(selectedRequest.counter_time_slot)}
                    </p>
                    {selectedRequest.counter_message && (
                      <p className="text-xs text-orange-300 mt-1 italic">"{selectedRequest.counter_message}"</p>
                    )}
                    <div className="flex gap-2 mt-3">
                      <button
                        onClick={async () => {
                          const token = localStorage.getItem("token");
                          const API_URL = process.env.REACT_APP_BACKEND_URL;
                          await fetch(`${API_URL}/api/admin/intake-requests/${selectedRequest.id}?status=confirmed`, {
                            method: "PUT",
                            headers: { Authorization: `Bearer ${token}` }
                          });
                          // Confirmer avec la date counter
                          await fetch(`${API_URL}/api/admin/intake-requests/${selectedRequest.id}`, {
                            method: "PUT",
                            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
                            body: JSON.stringify({ preferred_date: selectedRequest.counter_date, preferred_time_slot: selectedRequest.counter_time_slot })
                          }).catch(() => {});
                          import("sonner").then(({ toast }) => toast.success("Date du client acceptee"));
                          setDetailDialogOpen(false);
                          fetchRequests();
                        }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-emerald-500/20 hover:bg-emerald-500/30 text-emerald-400 text-xs font-medium transition-colors"
                      >
                        <CheckCircle2 className="w-3.5 h-3.5" /> Accepter cette date
                      </button>
                      <button
                        onClick={() => { setProposeTarget({ request: selectedRequest, type: "intake" }); setDetailDialogOpen(false); setProposeDateDialogOpen(true); }}
                        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 text-xs font-medium transition-colors"
                      >
                        <CalendarDays className="w-3.5 h-3.5" /> Re-proposer
                      </button>
                    </div>
                  </div>
                )}

                {selectedRequest.service_type && (
                  <div className={`rounded-lg p-3 border ${selectedRequest.service_type === "domicile" ? "bg-violet-500/10 border-violet-500/30" : "bg-blue-500/10 border-blue-500/30"}`}>
                    <p className="text-xs text-muted-foreground mb-1">Mode de prise en charge</p>
                    <p className="text-sm font-medium">{selectedRequest.service_type === "domicile" ? "A domicile" : "En atelier"}</p>
                    {selectedRequest.service_type === "domicile" && selectedRequest.address_street && (
                      <p className="text-xs text-muted-foreground mt-1">
                        {selectedRequest.address_street}
                        {selectedRequest.address_complement && `, ${selectedRequest.address_complement}`}
                        {selectedRequest.address_postal_code && `, ${selectedRequest.address_postal_code}`}
                        {selectedRequest.address_city && ` ${selectedRequest.address_city}`}
                      </p>
                    )}
                    {selectedRequest.proposed_date && (
                      <p className="text-xs text-amber-400 mt-1">Date proposee par admin : {selectedRequest.proposed_date}</p>
                    )}
                    {selectedRequest.counter_date && (
                      <p className="text-xs text-orange-400 mt-1">Contre-proposition client : {selectedRequest.counter_date}</p>
                    )}
                  </div>
                )}

                <div className="flex gap-2 pt-4">
                  {selectedRequest.status === "pending" && (
                    <>
                      <Button 
                        className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                        onClick={() => { handleStatusChange(selectedRequest, "confirmed"); setDetailDialogOpen(false); }}
                      >
                        <CheckCircle2 className="w-4 h-4 mr-2" />
                        Valider
                      </Button>
                      <Button
                        variant="outline"
                        className="flex-1 border-amber-500 text-amber-500 hover:bg-amber-500/10"
                        onClick={() => { setProposeTarget({ request: selectedRequest, type: "intake" }); setDetailDialogOpen(false); setProposeDateDialogOpen(true); }}
                      >
                        <CalendarDays className="w-4 h-4 mr-2" />
                        Autre date
                      </Button>
                      <Button 
                        variant="outline"
                        className="border-red-500 text-red-500 hover:bg-red-500/10"
                        onClick={() => { handleStatusChange(selectedRequest, "cancelled"); setDetailDialogOpen(false); }}
                      >
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                  {selectedRequest.status === "confirmed" && (
                    <Button 
                      className="flex-1 bg-primary"
                      onClick={() => { setDetailDialogOpen(false); openConvertDialog(selectedRequest); }}
                    >
                      <ArrowRight className="w-4 h-4 mr-2" />
                      Convertir en réparation
                    </Button>
                  )}
                  <Button 
                    variant="outline"
                    className="border-destructive text-destructive hover:bg-destructive/10"
                    onClick={() => { setDetailDialogOpen(false); openDeleteDialog(selectedRequest); }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Add Manual Request Dialog */}
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogContent className="bg-card border-border max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Plus className="w-5 h-5 text-primary" />
                Ajouter une demande manuellement
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Prénom *</Label>
                  <Input
                    value={newRequest.client_first_name}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, client_first_name: e.target.value }))}
                    placeholder="Jean"
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Nom *</Label>
                  <Input
                    value={newRequest.client_last_name}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, client_last_name: e.target.value }))}
                    placeholder="Dupont"
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Téléphone *</Label>
                <Input
                  value={newRequest.client_phone}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, client_phone: e.target.value }))}
                  placeholder="0696 XX XX XX"
                  className="bg-secondary/50"
                />
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <Input
                  type="email"
                  value={newRequest.client_email}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, client_email: e.target.value }))}
                  placeholder="jean@exemple.com"
                  className="bg-secondary/50"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Marque *</Label>
                  <Select value={newRequest.device_brand} onValueChange={(v) => setNewRequest(prev => ({ ...prev, device_brand: v }))}>
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue placeholder="Sélectionner..." />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border">
                      {BRANDS.map(brand => (
                        <SelectItem key={brand} value={brand}>{brand}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Modèle</Label>
                  <Input
                    value={newRequest.device_model}
                    onChange={(e) => setNewRequest(prev => ({ ...prev, device_model: e.target.value }))}
                    placeholder="iPhone 15..."
                    className="bg-secondary/50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Description du problème *</Label>
                <Textarea
                  value={newRequest.problem_description}
                  onChange={(e) => setNewRequest(prev => ({ ...prev, problem_description: e.target.value }))}
                  placeholder="Décrivez le problème..."
                  className="bg-secondary/50"
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={newRequest.preferred_date}
                    onChange={(e) => {
                      if (!isWeekend(e.target.value)) {
                        setNewRequest(prev => ({ ...prev, preferred_date: e.target.value }));
                      } else {
                        toast.error("Les créneaux sont disponibles du lundi au vendredi uniquement");
                      }
                    }}
                    min={getMinDate()}
                    className="bg-secondary/50"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Créneau horaire *</Label>
                  <Select value={newRequest.preferred_time_slot} onValueChange={(v) => setNewRequest(prev => ({ ...prev, preferred_time_slot: v }))}>
                    <SelectTrigger className="bg-secondary/50">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-card border-border max-h-[200px]">
                      {TIME_SLOTS.map(slot => (
                        <SelectItem key={slot.value} value={slot.value}>{slot.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <p className="text-xs text-muted-foreground">
                Horaires: Lundi - Vendredi, 9h - 17h (créneaux de 30 min)
              </p>

              <div className="flex gap-3 pt-4">
                <Button variant="outline" onClick={() => setAddDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button onClick={handleAddRequest} disabled={submitting} className="flex-1 bg-primary">
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Ajouter"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>

        {/* Convert to Repair Dialog */}
        <Dialog open={convertDialogOpen} onOpenChange={(open) => { 
          setConvertDialogOpen(open); 
          if (!open) {
            setSerialNumber(""); 
            setConversionPhotos([]);
            setShowQrCode(false);
            setQrCodeData(null);
          }
        }}>
          <DialogContent className="bg-card border-border max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2">
                <Wrench className="w-5 h-5 text-emerald-500" />
                Convertir en réparation
              </DialogTitle>
            </DialogHeader>
            {selectedRequest && (
              <div className="space-y-4 mt-4">
                <div className="bg-secondary/30 rounded-lg p-4 space-y-2">
                  <p><strong>Client :</strong> {selectedRequest.client_name}</p>
                  <p><strong>Appareil :</strong> {selectedRequest.device_brand} {selectedRequest.device_model}</p>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Smartphone className="w-4 h-4" />
                    Numéro de série / IMEI *
                  </Label>
                  <Input
                    value={serialNumber}
                    onChange={(e) => setSerialNumber(e.target.value.replace(/\s/g, ''))}
                    placeholder="15 caractères (IMEI)"
                    className="bg-secondary/50 font-mono"
                  />
                </div>

                {/* Photos Section - MANDATORY */}
                <div className="space-y-3 border border-amber-500/30 rounded-lg p-4 bg-amber-500/5">
                  <Label className="flex items-center gap-2 text-amber-400">
                    <Camera className="w-4 h-4" />
                    Photos de l'appareil * (obligatoire)
                  </Label>
                  
                  {/* Photos Preview */}
                  {conversionPhotos.length > 0 && (
                    <div className="grid grid-cols-4 gap-2">
                      {conversionPhotos.map(photo => (
                        <div key={photo.id} className="relative group">
                          <img 
                            src={photo.data} 
                            alt="Photo" 
                            className="w-full h-16 object-cover rounded-md"
                          />
                          <button
                            onClick={() => removeConversionPhoto(photo.id)}
                            className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 rounded-full text-white text-xs flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  <div className="flex gap-2">
                    {/* File upload button */}
                    <label className="flex-1">
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        onChange={handleConversionPhotoUpload}
                        className="hidden"
                      />
                      <Button variant="outline" className="w-full" asChild>
                        <span>
                          <Upload className="w-4 h-4 mr-2" />
                          Ajouter photos
                        </span>
                      </Button>
                    </label>
                    
                    {/* QR Code button */}
                    <Button 
                      variant="outline" 
                      onClick={generateConversionQrCode}
                      disabled={qrCodeLoading}
                      className="border-primary text-primary"
                    >
                      {qrCodeLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <>
                          <QrCode className="w-4 h-4 mr-2" />
                          QR Code
                        </>
                      )}
                    </Button>
                  </div>
                  
                  {/* QR Code Display */}
                  {showQrCode && qrCodeData && (
                    <div className="mt-3 p-4 bg-white rounded-lg flex flex-col items-center">
                      <QRCodeSVG value={qrCodeData.url} size={150} />
                      <p className="text-xs text-gray-500 mt-2 text-center">
                        Scannez pour ajouter des photos depuis votre téléphone
                      </p>
                      <div className="flex gap-2 mt-2">
                        <Button 
                          variant="outline" 
                          size="sm" 
                          onClick={checkForUploadedPhotos}
                          className="text-xs text-primary border-primary"
                        >
                          <RefreshCw className="w-3 h-3 mr-1" />
                          Rafraîchir
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => setShowQrCode(false)}
                          className="text-xs"
                        >
                          Masquer
                        </Button>
                      </div>
                    </div>
                  )}
                  
                  <p className="text-xs text-muted-foreground">
                    {conversionPhotos.length === 0 
                      ? "⚠️ Au moins une photo est requise pour créer la réparation"
                      : `✓ ${conversionPhotos.length} photo(s) ajoutée(s)`
                    }
                  </p>
                </div>

                <div className="flex gap-3 pt-4">
                  <Button variant="outline" onClick={() => setConvertDialogOpen(false)} className="flex-1">
                    Annuler
                  </Button>
                  <Button 
                    onClick={handleConvertToRepair} 
                    disabled={submitting || !serialNumber || conversionPhotos.length === 0} 
                    className="flex-1 bg-emerald-500 hover:bg-emerald-600"
                  >
                    {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Créer la réparation"}
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="bg-card border-border max-w-sm">
            <DialogHeader>
              <DialogTitle className="font-chivo flex items-center gap-2 text-destructive">
                <Trash2 className="w-5 h-5" />
                Supprimer la demande
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <p className="text-muted-foreground">
                Êtes-vous sûr de vouloir supprimer cette demande de prise en charge ?
              </p>
              {selectedRequest && (
                <div className="bg-secondary/30 rounded-lg p-3">
                  <p className="font-medium">{selectedRequest.client_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedRequest.device_brand} {selectedRequest.device_model}</p>
                </div>
              )}
              <div className="flex gap-3">
                <Button variant="outline" onClick={() => setDeleteDialogOpen(false)} className="flex-1">
                  Annuler
                </Button>
                <Button 
                  onClick={handleDelete} 
                  disabled={submitting} 
                  className="flex-1 bg-destructive hover:bg-destructive/90"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Supprimer"}
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      {/* Dialog Proposer une autre date */}
      <Dialog open={proposeDateDialogOpen} onOpenChange={setProposeDateDialogOpen}>
        <DialogContent className="bg-card border-border max-w-sm">
          <DialogHeader>
            <DialogTitle className="font-chivo flex items-center gap-2">
              <CalendarDays className="w-5 h-5 text-amber-500" />
              Proposer une autre date
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            {proposeTarget && (
              <div className="p-3 rounded-lg bg-secondary/50 text-sm">
                <p className="font-medium">{proposeTarget.request?.client_name}</p>
                <p className="text-muted-foreground">{proposeTarget.request?.device_brand} {proposeTarget.request?.device_model}</p>
                <p className="text-muted-foreground">Date demandee : {proposeTarget.request?.preferred_date}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Nouvelle date proposee *</Label>
              <Input
                type="date"
                value={proposedDate}
                onChange={(e) => setProposedDate(e.target.value)}
                min={new Date().toISOString().split("T")[0]}
                className="bg-secondary/50"
              />
            </div>
            <div className="space-y-2">
              <Label>Creneau</Label>
              <select
                value={proposedTime}
                onChange={(e) => setProposedTime(e.target.value)}
                className="w-full p-2 rounded-md border border-border bg-secondary/50 text-sm"
              >
                <optgroup label="Horaires d'ouverture">
                  {ADMIN_SLOTS.filter(s => !s.label.includes("hors")).map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
                <optgroup label="⚡ Hors horaires">
                  {ADMIN_SLOTS.filter(s => s.label.includes("hors")).map(s => (
                    <option key={s.value} value={s.value}>{s.label}</option>
                  ))}
                </optgroup>
              </select>
            </div>
            <div className="space-y-2">
              <Label>Message au client (optionnel)</Label>
              <Textarea
                value={proposedMessage}
                onChange={(e) => setProposedMessage(e.target.value)}
                placeholder="Ex : Le creneau demande n'est pas disponible, nous vous proposons..."
                className="bg-secondary/50 text-sm"
                rows={3}
              />
            </div>
            <div className="flex gap-3 pt-2">
              <Button variant="outline" onClick={() => setProposeDateDialogOpen(false)} className="flex-1">
                Annuler
              </Button>
              <Button
                onClick={handleProposeDate}
                disabled={proposeSubmitting || !proposedDate}
                className="flex-1 bg-amber-500 hover:bg-amber-600 text-white"
              >
                {proposeSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Envoyer par email"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      </div>
    </MainLayout>
  );
}